/**
 * ReadingSheet — rules in the organiser's own words, read back as cards.
 *
 * The AI reads the words once (on the server, section by section; this sheet
 * polls until it finishes). Each readback card shows our sentence with
 * tappable blanks, the organiser's own words, a worked example and any clash
 * question — all written by our code, not the AI. Nothing counts until the
 * organiser adds it to the draft. "Add all" adds every card that needs no
 * answer; the rest are added one at a time.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import RuleSheet from './RuleSheet';
import RuleEditor from './RuleEditor';
import { IdeasList } from './IdeasList';
import { readApi, errorText } from './api';
import { KIND_META } from './ruleText';

const OUTCOME = {
  added: { text: 'Added', icon: 'ph-check-circle', color: '#1F7A75' },
  edited: { text: 'Added', icon: 'ph-check-circle', color: '#1F7A75' },
  kept_as_text: { text: 'Added as text', icon: 'ph-file-text', color: '#6B7280' },
  deleted: { text: 'Not added', icon: 'ph-x-circle', color: '#9CA3AF' },
  retried: { text: 'Read again below', icon: 'ph-arrow-bend-down-right', color: '#9CA3AF' },
};

function Sentence({ card, catalogue, onTapBlank }) {
  const block = catalogue.blocks[card.block];
  if (card.block === 'text') return <span>{card.source_text}</span>;
  return (
    <>
      {card.segments.filter((g) => g.show || (g.missing && block?.params?.[g.param]?.required)).map((g, i) => (
        'param' in g
          ? (
            <button key={i} type="button" onClick={onTapBlank} disabled={!onTapBlank}
              className={`mx-0.5 px-1.5 rounded-md font-bold ${g.missing
                ? 'bg-[#FDF5D3] border border-dashed border-[#F4D341] text-[#2B3A67]' : 'bg-[#E9EBF1] text-[#2B3A67]'}`}>
              {g.missing ? '?' : g.text}
            </button>
          )
          : <span key={i}>{g.text}</span>
      ))}
      {card.stages && <span className="text-gray-500"> ({card.stages.map((s) => catalogue.stage_labels[s]).join(', ')} only)</span>}
    </>
  );
}

function OwnWords({ card }) {
  const src = card.source_text || card.quote || '';
  const q = (card.quote || '').trim();
  const at = q ? src.toLowerCase().indexOf(q.toLowerCase()) : -1;
  return (
    <p className="mt-2 text-xs text-gray-600 leading-relaxed">
      <span className="font-bold text-gray-500">From your words: </span>
      {at < 0 || q.length === src.length ? <>“{src}”</> : (
        <>“{src.slice(0, at)}<mark className="bg-[#FDF5D3] text-inherit rounded px-0.5">{src.slice(at, at + q.length)}</mark>{src.slice(at + q.length)}”</>
      )}
    </p>
  );
}

function NumberAnswer({ question, onAnswer, busy }) {
  const [value, setValue] = useState('');
  return (
    <div className="flex items-center gap-2 mt-1.5">
      <input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} placeholder="Number"
        className="w-24 h-10 px-3 rounded-xl border border-gray-200 text-sm font-bold text-[#2B3A67]" />
      <button type="button" disabled={busy || value.trim() === '' || Number.isNaN(Number(value))}
        onClick={() => onAnswer({ key: question.key, value: Number(value) })}
        className="h-10 px-4 rounded-xl bg-[#2B3A67] text-white text-sm font-bold disabled:opacity-40">OK</button>
    </div>
  );
}

function ReadbackCard({ card, catalogue, busy, act }) {
  const [retrying, setRetrying] = useState(false);
  const [correction, setCorrection] = useState('');
  const kind = KIND_META[card.kind] || KIND_META.text;
  const done = OUTCOME[card.outcome];
  const isText = card.block === 'text';
  const edge = done ? '#E5E7EB' : card.needs_answer ? '#F4D341' : card.not_understood ? '#E54562' : isText ? '#9CA3AF' : '#3AAFA9';

  if (done) {
    return (
      <li className="rounded-2xl bg-white border border-gray-100 px-4 py-3 flex items-start gap-3 opacity-70" data-testid="read-card-done">
        <i className={`ph-bold ${done.icon} mt-0.5`} style={{ color: done.color }} />
        <span className="flex-1 min-w-0 text-sm text-gray-600 truncate">{isText ? card.source_text : card.sentence}</span>
        <span className="text-[11px] font-bold whitespace-nowrap" style={{ color: done.color }}>{done.text}</span>
      </li>
    );
  }
  return (
    <li className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden" style={{ borderLeft: `5px solid ${edge}` }}
      data-testid="read-card">
      <div className="px-4 pt-3 pb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-1" style={{ color: kind.color, background: kind.bg }}>
            <i className={`ph-bold ${kind.icon}`} />{card.not_understood ? 'Not understood yet' : kind.label}
          </span>
          {!isText && <span className="text-[11px] font-semibold text-gray-400">{card.label}</span>}
          {card.needs_answer && <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-[#F4D341] text-[#2B3A67]">Needs your answer</span>}
          {card.same_as && <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-[#E9EBF1] text-[#2B3A67]">Already in your rulebook</span>}
        </div>

        <p className="mt-2 text-[15px] leading-relaxed text-[#2B3A67]">
          <Sentence card={card} catalogue={catalogue} onTapBlank={isText ? null : () => act.edit(card)} />
        </p>
        {!isText && <OwnWords card={card} />}

        {card.example && (
          <p className="mt-2 text-xs rounded-lg bg-[#E6F5F4] text-[#1F7A75] px-2.5 py-1.5" data-testid="read-card-example">
            <i className="ph-bold ph-lightbulb" /> {card.example}
          </p>
        )}
        {card.replaces_sentence && (
          <p className="mt-2 text-xs text-[#B3263F]"><i className="ph-bold ph-swap" /> Replaces: {card.replaces_sentence}</p>
        )}
        {card.disagreement && (
          <div className="mt-2 text-xs rounded-lg bg-[#FDF5D3] text-[#2B3A67] px-2.5 py-1.5 space-y-0.5">
            <p className="font-bold">We read your image twice and got two versions:</p>
            <p>1. {card.disagreement.first}</p>
            <p>2. {card.disagreement.second || 'not found'}</p>
          </div>
        )}
        {card.question && (
          <p className="mt-2 text-xs font-semibold text-[#2B3A67] bg-[#FDF5D3] rounded-lg px-2.5 py-1.5">
            <i className="ph-bold ph-question" /> {card.question}
          </p>
        )}
        {(card.questions || []).map((q) => (
          <div key={q.key} className="mt-2 rounded-lg border border-[#F4D341] px-2.5 py-2" data-testid="clash-question">
            <p className="text-xs font-bold text-[#2B3A67]">{q.text}</p>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {q.options.map((label, i) => (
                <button key={i} type="button" disabled={busy} onClick={() => act.answer(card, { key: q.key, option: i })}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold border border-[#2B3A67]/30 text-[#2B3A67] bg-white hover:border-[#2B3A67] text-left">
                  {label}
                </button>
              ))}
            </div>
            {q.type === 'number' && <NumberAnswer question={q} busy={busy} onAnswer={(body) => act.answer(card, body)} />}
          </div>
        ))}
        {card.notes?.length > 0 && !card.not_understood && (
          <ul className="mt-2 text-[11px] text-gray-500 space-y-0.5">
            {card.notes.map((n, i) => <li key={i}><i className="ph ph-info" /> {n}</li>)}
          </ul>
        )}

        {retrying && (
          <div className="mt-3">
            <textarea value={correction} onChange={(e) => setCorrection(e.target.value)} maxLength={500} autoFocus
              placeholder="Say what you meant, e.g. “only in the final” or “3 runs, not 2”"
              className="w-full min-h-[70px] p-3 rounded-xl border border-[#3AAFA9] text-sm" />
            <div className="flex gap-2 mt-1.5">
              <button type="button" onClick={() => setRetrying(false)} className="h-10 px-4 rounded-xl text-sm font-semibold text-gray-500">Cancel</button>
              <button type="button" disabled={busy || !correction.trim()}
                onClick={async () => { if (await act.retry(card, correction)) setRetrying(false); }}
                className="flex-1 h-10 rounded-xl bg-[#2B3A67] text-white text-sm font-bold disabled:opacity-40">
                {busy ? 'Reading…' : 'Read it again'}
              </button>
            </div>
          </div>
        )}
      </div>

      {!retrying && (
        <div className="flex flex-wrap gap-2 px-4 pb-3">
          {isText ? (
            <>
              <button type="button" disabled={busy} onClick={() => act.keepAsText(card)} data-testid="read-card-add"
                className="h-10 px-4 rounded-xl bg-[#2B3A67] text-white text-sm font-bold disabled:opacity-40">Add as text</button>
              <button type="button" disabled={busy} onClick={() => act.turnIntoRule(card)}
                className="h-10 px-4 rounded-xl border border-[#2B3A67] text-[#2B3A67] text-sm font-semibold">Turn into a rule</button>
            </>
          ) : (
            <>
              <button type="button" disabled={busy || (card.questions || []).length > 0} onClick={() => act.add(card)}
                data-testid="read-card-add" title={(card.questions || []).length ? 'Answer the question first' : undefined}
                className="h-10 px-4 rounded-xl bg-[#2B3A67] text-white text-sm font-bold disabled:opacity-40">
                {card.same_as ? 'Add anyway' : 'Add'}
              </button>
              <button type="button" disabled={busy} onClick={() => setRetrying(true)}
                className="h-10 px-3 rounded-xl border border-[#2B3A67] text-[#2B3A67] text-sm font-semibold">Not what I meant</button>
              <button type="button" disabled={busy} onClick={() => act.keepAsText(card)}
                className="h-10 px-3 rounded-xl text-[#2B3A67] text-sm font-semibold">Keep as text</button>
              {card.kind !== 'umpire' && (
                <button type="button" disabled={busy} onClick={() => act.umpire(card)}
                  className="h-10 px-3 rounded-xl text-[#2B3A67] text-sm font-semibold">Make it an umpire button</button>
              )}
            </>
          )}
          <button type="button" disabled={busy} onClick={() => act.remove(card)} aria-label="Delete"
            className="h-10 px-3 rounded-xl text-[#B3263F] text-sm font-semibold ml-auto">Delete</button>
        </div>
      )}
    </li>
  );
}

export default function ReadingSheet({ open, eventId, importId, catalogue, onClose, onRulebook }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);       // card open in the rule editor
  const [picking, setPicking] = useState(null);       // text card being turned into a rule
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const out = await readApi.get(eventId, importId);
      setData(out.import);
      return out.import;
    } catch (e) {
      toast.error(errorText(e));
      return null;
    }
  }, [eventId, importId]);

  // Poll while the AI is reading; cards appear section by section
  useEffect(() => {
    if (!open || !importId) return undefined;
    let stopped = false;
    const tick = async () => {
      const imp = await load();
      if (!stopped && imp?.status === 'reading') timer.current = setTimeout(tick, 1200);
    };
    setData(null);
    tick();
    return () => { stopped = true; clearTimeout(timer.current); };
  }, [open, importId, load]);

  const run = async (fn, okText) => {
    setBusy(true);
    try {
      const out = await fn();
      if (out.import) setData(out.import);
      if (out.rulebook) onRulebook(out.rulebook);
      if (okText) toast.success(typeof okText === 'function' ? okText(out) : okText);
      return true;
    } catch (e) {
      toast.error(errorText(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const act = {
    edit: (card) => setEditing(card),
    add: (card) => run(() => readApi.add(eventId, importId, card.id), 'Rule added to the draft'),
    keepAsText: (card) => run(() => readApi.keepAsText(eventId, importId, card.id), 'Added as rulebook text'),
    remove: (card) => run(() => readApi.remove(eventId, importId, card.id)),
    answer: (card, body) => run(() => readApi.answer(eventId, importId, card.id, body)),
    retry: (card, correction) => run(() => readApi.retry(eventId, importId, card.id, correction)),
    umpire: (card) => run(() => readApi.editCard(eventId, importId, card.id, {
      block: 'umpire_penalty', params: { offence: (card.quote || card.source_text || '').slice(0, 80) },
    })),
    turnIntoRule: (card) => setPicking(card),
  };

  const cards = data?.cards || [];
  const reading = data?.status === 'reading';
  const waiting = cards.filter((c) => !c.outcome);
  const doneCards = cards.filter((c) => c.outcome);

  return (
    <RuleSheet open={open} onClose={onClose} testId="reading-sheet"
      title={reading ? 'Reading your rules…' : 'Check what we read'}
      subtitle={data ? (reading
        ? (data.reading_images && !data.words ? 'Reading your image (twice, to be sure)…'
          : `${data.sections_done} of ${data.sections_total || 1} ${data.sections_total === 1 ? 'part' : 'parts'} read`)
        : `${waiting.length} to check · ${doneCards.filter((c) => ['added', 'edited', 'kept_as_text'].includes(c.outcome)).length} added`) : 'Loading…'}
      footer={data && !reading && waiting.length > 0 ? (
        <button type="button" disabled={busy || !data.can_add_all}
          onClick={() => run(() => readApi.addAll(eventId, importId), (out) => `${out.added} ${out.added === 1 ? 'rule' : 'rules'} added to the draft`)}
          className="w-full h-12 rounded-xl bg-[#2B3A67] text-white font-bold disabled:opacity-40" data-testid="read-add-all">
          {data.can_add_all ? `Add all that need no answer (${data.can_add_all})` : 'Answer the highlighted cards'}
        </button>
      ) : data && !reading ? (
        <button type="button" onClick={onClose} className="w-full h-12 rounded-xl bg-[#2B3A67] text-white font-bold">Done</button>
      ) : null}>
      {!data && <div className="h-32 rounded-2xl bg-gray-100 animate-pulse" />}
      {data && (
        <div className="space-y-3 pb-2">
          {reading && (
            <div className="rounded-xl bg-[#E9EBF1] h-2 overflow-hidden" aria-hidden="true">
              <div className="h-full bg-[#3AAFA9] transition-all"
                style={{ width: `${data.sections_total ? Math.max(8, (100 * data.sections_done) / data.sections_total) : 15}%` }} />
            </div>
          )}
          {data.message && (
            <p className="text-sm rounded-xl bg-[#FDF5D3] text-[#2B3A67] px-3 py-2" data-testid="read-message">
              <i className="ph-bold ph-info" /> {data.message}
            </p>
          )}
          {!reading && cards.length === 0 && !data.message && (
            <p className="text-sm text-gray-500">We didn't find any rules in that. Try typing them instead.</p>
          )}
          <ul className="space-y-3">
            {waiting.map((c) => <ReadbackCard key={c.id} card={c} catalogue={catalogue} busy={busy} act={act} />)}
          </ul>
          {reading && <div className="h-24 rounded-2xl bg-gray-100 animate-pulse" />}
          {doneCards.length > 0 && (
            <ul className="space-y-2 pt-1">
              {doneCards.map((c) => <ReadbackCard key={c.id} card={c} catalogue={catalogue} busy={busy} act={act} />)}
            </ul>
          )}
          {data.words && (data.way_in === 'image' || data.way_in === 'pdf' || data.way_in === 'docx') && !reading && (
            <details className="text-xs text-gray-500">
              <summary className="cursor-pointer font-semibold">What we read from {data.file_name || 'your file'}</summary>
              <p className="mt-1 whitespace-pre-wrap">{data.words}</p>
            </details>
          )}
        </div>
      )}

      {editing && (
        <RuleEditor key={editing.id} open catalogue={catalogue} saving={busy}
          rule={{ block: editing.block, params: editing.params, stages: editing.stages, source_text: editing.source_text, section: editing.section }}
          onClose={() => setEditing(null)}
          onSave={async (rule) => {
            if (await run(() => readApi.editCard(eventId, importId, editing.id, { params: rule.params, stages: rule.stages, source_text: rule.source_text }))) setEditing(null);
          }} />
      )}
      <RuleSheet open={!!picking} onClose={() => setPicking(null)} title="Turn into a rule"
        subtitle={picking ? `“${(picking.source_text || '').slice(0, 90)}”` : ''} testId="turn-into-rule">
        {picking && (
          <IdeasList catalogue={catalogue} onPick={async (rule) => {
            const card = picking;
            setPicking(null);
            if (await run(() => readApi.editCard(eventId, importId, card.id, { block: rule.block, params: rule.params || {} }))) {
              const fresh = await load();
              const next = fresh?.cards.find((c) => c.id === card.id);
              if (next) setEditing(next);
            }
          }} />
        )}
      </RuleSheet>
    </RuleSheet>
  );
}
